import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Facebook, Instagram, Linkedin, Save, RotateCcw, Phone, Mail, MapPin } from "lucide-react";

const FIELDS = [
  { key: "facebook", label: "Facebook", Icon: Facebook, placeholder: "https://www.facebook.com/yourpage" },
  { key: "instagram", label: "Instagram", Icon: Instagram, placeholder: "https://www.instagram.com/yourprofile" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, placeholder: "https://www.linkedin.com/company/yourcompany" },
];

export default function SiteSettingsManager() {
  const [links, setLinks] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/site-settings/social").then(({ data }) => { setLinks(data.links); setDefaults(data.defaults); setMeta({ updated_at: data.updated_at, updated_by: data.updated_by }); }).catch(() => toast.error("Could not load settings"));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-settings/social", links);
      setLinks(data.links);
      toast.success("Social links saved — footer updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const [contact, setContact] = useState(null);
  const [contactMeta, setContactMeta] = useState({});
  const [savingContact, setSavingContact] = useState(false);
  const loadContact = () => api.get("/admin/site-settings/contact").then(({ data }) => { setContact(data.contact); setContactMeta({ updated_at: data.updated_at, updated_by: data.updated_by }); }).catch(() => {});
  useEffect(() => { loadContact(); }, []);
  const saveContact = async (e) => {
    e.preventDefault();
    setSavingContact(true);
    try {
      const { data } = await api.put("/admin/site-settings/contact", contact);
      setContact(data.contact);
      toast.success("Contact details saved — footer updated");
      loadContact();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSavingContact(false); }
  };

  if (!links) return <div className="text-sm text-[#7D8C85]">Loading…</div>;
  return (
    <div className="space-y-8" data-testid="admin-site-settings">
      {contact && (
        <form onSubmit={saveContact} className="bg-white border border-[#E8ECE9] p-6 max-w-2xl" data-testid="contact-settings-form">
          <div className="tracking-overline text-[10px] text-[#264839] mb-3">Site settings</div>
          <h2 className="font-serif text-2xl text-[#11211A] mb-1">Footer contact details</h2>
          <p className="text-sm text-[#7D8C85] mb-6">Phone, email and address shown under the company blurb in the footer. Leave any field blank to hide it. Changes are live immediately.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold"><Phone className="w-3.5 h-3.5" /> Phone</label>
              <input type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+61 2 1234 5678" className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white" data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold"><Mail className="w-3.5 h-3.5" /> Email</label>
              <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="hello@accommodationsuppliers.com" className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white" data-testid="contact-email-input" />
            </div>
          </div>
          <div className="mt-4">
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold"><MapPin className="w-3.5 h-3.5" /> Business address</label>
            <textarea value={contact.address || ""} onChange={(e) => setContact({ ...contact, address: e.target.value })} rows={3} maxLength={200} placeholder={"QuArete Pty Ltd\nLevel 1, 123 Example Street\nSydney NSW 2000, Australia"} className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white" data-testid="contact-address-input" />
            <div className="text-[11px] text-[#7D8C85] mt-1">One line per row — shown exactly as typed. Leave blank to hide.</div>
          </div>
          <div className="flex items-center justify-between mt-6">
            <div className="text-[11px] text-[#7D8C85]" data-testid="contact-last-updated">{contactMeta.updated_at ? `Last updated ${contactMeta.updated_at.replace("T", " ").slice(0, 16)} by ${contactMeta.updated_by || "admin"}` : "Using defaults"}</div>
            <button type="submit" disabled={savingContact} className="btn-primary text-xs disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="contact-save"><Save className="w-3.5 h-3.5" /> {savingContact ? "Saving…" : "Save contact details"}</button>
          </div>
        </form>
      )}
      <form onSubmit={save} className="bg-white border border-[#E8ECE9] p-6 max-w-2xl">
        <div className="tracking-overline text-[10px] text-[#264839] mb-3">Site settings</div>
        <h2 className="font-serif text-2xl text-[#11211A] mb-1">Footer social links</h2>
        <p className="text-sm text-[#7D8C85] mb-6">Shown as icons in the site footer on every page. Leave a field blank to hide that icon. Changes are live immediately.</p>
        <div className="space-y-4">
          {FIELDS.map(({ key, label, Icon, placeholder }) => (
            <div key={key}>
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold"><Icon className="w-3.5 h-3.5" /> {label}</label>
              <div className="flex gap-2">
                <input type="url" value={links[key]} onChange={(e) => setLinks({ ...links, [key]: e.target.value })} placeholder={placeholder} className="flex-1 px-3 py-2 border border-[#C4CFC9] text-sm bg-white" data-testid={`social-${key}-input`} />
                <button type="button" onClick={() => setLinks({ ...links, [key]: defaults[key] || "" })} title="Reset to placeholder" className="px-2 border border-[#C4CFC9] text-[#7D8C85] hover:text-[#264839] hover:border-[#264839] transition" data-testid={`social-${key}-reset`}><RotateCcw className="w-3.5 h-3.5" /></button>
              </div>
              {links[key] && links[key] === defaults[key] && <div className="text-[11px] text-[#8A6508] mt-1">Placeholder URL — replace with the real profile when ready.</div>}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-6">
          <div className="text-[11px] text-[#7D8C85]" data-testid="social-last-updated">{meta.updated_at ? `Last updated ${meta.updated_at.replace("T", " ").slice(0, 16)} by ${meta.updated_by || "admin"}` : "Using placeholder defaults"}</div>
          <button type="submit" disabled={saving} className="btn-primary text-xs disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="social-save"><Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save links"}</button>
        </div>
      </form>
    </div>
  );
}
